import { Icon, Tooltip } from "@deligoo/ui"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"

import { useAppSelector } from "@/App"
import { selectDriverById } from "@/store/drivers"
import { selectCurrentTeamStatsKey } from "@/store/teamsStats"
import { Driver, Subteam } from "@/types"
import { getIconFor } from "@/utils"

import cls from "./DriverOverview.module.scss"

type DriverOverviewProps = { driverId: Driver["id"]; size?: "small" | "medium" }

export const DriverOverview = ({ driverId, size = "small" }: DriverOverviewProps) => {
  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)

  const currentTeamSubteams = useAppSelector(selectCurrentTeamStatsKey("sub_teams"))

  const { t } = useTranslation()

  if (!driver) return null

  const paymentForms = ["card", "cash"] as Driver["payment_forms"]

  const belongsToEverySubteam = currentTeamSubteams?.length === driver.sub_teams.length

  return (
    <ul className={cls.overview}>
      {Boolean(driver.working_hours || driver.updated_working_hours || driver.today_breaks?.length) && (
        <Tooltip
          content={
            <>
              {Boolean(driver.working_hours || driver.updated_working_hours) && (
                <div>{`${t("drivers:work")}: ${driver.updated_working_hours || driver.working_hours}`}</div>
              )}
              {Boolean(driver.today_breaks?.length) && (
                <div>
                  {t("drivers:break")}: {getDriverBreaks(driver.today_breaks)}
                </div>
              )}
            </>
          }
        >
          <li className={cls.item}>
            <Icon name="clock" size={size} />
          </li>
        </Tooltip>
      )}

      <Tooltip content={t(`drivers:${driver.transport_type}`)}>
        <li className={cls.item}>
          <Icon name={getIconFor(driver.transport_type)} size={size} />
        </li>
      </Tooltip>

      {paymentForms.map((paymentForm) => (
        <Tooltip key={paymentForm} content={t(`drivers:${paymentForm}`)}>
          <li className={cls.item}>
            <Icon
              color={driver.payment_forms.includes(paymentForm) ? "success" : "alert"}
              name={getIconFor(paymentForm)}
              size={size}
            />
          </li>
        </Tooltip>
      ))}

      <Tooltip content={<SubteamsList subteams={driver.sub_teams} />}>
        <li className={cls.item}>
          <Icon name="users" size={size} color={belongsToEverySubteam ? "success" : undefined} />
          {!belongsToEverySubteam && <span className="text-primary ml-2">{driver.sub_teams?.length}</span>}
        </li>
      </Tooltip>
    </ul>
  )
}

const SubteamsList = ({ subteams }: { subteams: Subteam[] }) => {
  return (
    <ul className={cls.subteamsList}>
      {subteams.map((subteam) => (
        <li key={subteam.id}>{subteam.name}</li>
      ))}
    </ul>
  )
}

function getDriverBreaks(todayBreaks: Driver["today_breaks"]) {
  return todayBreaks.map(({ start_time, end_time, past, next }, index) => (
    <span key={`${start_time}${end_time}`} className={clsx(past && cls.past, next && cls.next)}>
      {start_time} - {end_time}
      {index < todayBreaks.length - 1 && ", "}
    </span>
  ))
}
